'use client';

import { CaretDown, Check } from '@phosphor-icons/react/ssr';
import type { ComponentProps, ReactNode } from 'react';

import { tv } from '../../lib/tv';
import { Badge } from './badge';

export type FilterChipProps = Omit<ComponentProps<'button'>, 'children'> & {
  /**
   * Figma State=Selected: Pale Sage fill, Deep Olive border and a check icon.
   * A plain chip is a toggle button (`aria-pressed`).
   */
  selected?: boolean;
  /** Called with the new state when a plain (non-dropdown) chip is pressed. */
  onSelectedChange?: (selected: boolean) => void;
  /** Figma "Count": number of matching items, shown as a small Badge. */
  count?: number;
  /**
   * Figma "Dropdown": adds the caret; the chip opens a Popover of options
   * instead of toggling (`<PopoverTrigger asChild><FilterChip dropdown …/>`).
   */
  dropdown?: boolean;
  /** Figma "Label", e.g. "Ready". */
  children: ReactNode;
};

/**
 * Figma Filter Chip (92:105): 32 tall, radius/full, 12px sides, gap 6,
 * Label/Small, 1px inside stroke. Focus: 2px border/focus stroke, drawn as
 * border + 1px inset ring so the chip doesn't grow.
 *
 * Motion: a 0.97 press (as Button). The check stays mounted in a slot that
 * opens from 0 to its width (grid 0fr → 1fr, with a -6px margin cancelling
 * the gap while closed) as the icon fades and grows in - so the chip widens
 * smoothly instead of jumping. Reduced motion switches instantly.
 */
export const filterChipStyles = tv({
  slots: {
    root: [
      'group/chip inline-flex h-8 w-fit shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3 whitespace-nowrap outline-none',
      'text-label-sm border-border-default bg-bg-surface text-text-primary',
      'transition-[background-color,border-color,box-shadow,scale] duration-[120ms] ease-out',
      'hover:border-border-strong-hover hover:bg-bg-subtle',
      'enabled:active:scale-[0.97] motion-reduce:enabled:active:scale-100',
      'data-selected:border-brand-strong data-selected:bg-brand-subtle data-selected:text-text-brand',
      'focus-visible:border-border-focus focus-visible:inset-ring-1 focus-visible:inset-ring-border-focus',
      'disabled:cursor-not-allowed disabled:border-border-subtle disabled:bg-bg-disabled disabled:text-text-disabled',
    ],
    checkSlot: [
      'grid -ml-1.5 grid-cols-[0fr] overflow-hidden group-data-selected/chip:ml-0 group-data-selected/chip:grid-cols-[1fr]',
      'transition-[grid-template-columns,margin] duration-200 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none',
    ],
    check: [
      'text-brand-strong block size-3.5 shrink-0',
      'scale-60 opacity-0 group-data-selected/chip:scale-100 group-data-selected/chip:opacity-100',
      'transition-[scale,opacity] duration-160 ease-out motion-reduce:transition-none',
    ],
    // Selected: Brand count on a bg/surface fill (Figma override).
    count: 'group-data-selected/chip:bg-bg-surface',
    caret: 'text-icon-secondary size-3.5 shrink-0 group-disabled/chip:text-icon-tertiary',
  },
});

/**
 * Toggleable filter in a chip row above a list - "Ready", "Mine", "9:16"
 * (Figma: Components ↳ Tag ↳ Filter Chip). Selected is shown by fill, border
 * and a check icon, not colour alone.
 */
export function FilterChip({
  selected = false,
  onSelectedChange,
  count,
  dropdown = false,
  className,
  children,
  onClick,
  ...props
}: FilterChipProps) {
  const styles = filterChipStyles();
  return (
    <button
      type="button"
      data-slot="filter-chip"
      data-selected={selected || undefined}
      data-disabled={props.disabled || undefined}
      aria-pressed={dropdown ? undefined : selected}
      className={styles.root({ className })}
      onClick={(e) => {
        onClick?.(e);
        if (!dropdown && !e.defaultPrevented) onSelectedChange?.(!selected);
      }}
      {...props}
    >
      <span aria-hidden data-slot="filter-chip-check" className={styles.checkSlot()}>
        {/* A grid track can't shrink below a fixed-width item: wrap the icon. */}
        <span className="min-w-0">
          <Check className={styles.check()} />
        </span>
      </span>
      {children}
      {count !== undefined && (
        <Badge tone={selected ? 'brand' : 'neutral'} className={styles.count()}>
          {count}
        </Badge>
      )}
      {dropdown && (
        <CaretDown aria-hidden data-slot="filter-chip-caret" className={styles.caret()} />
      )}
    </button>
  );
}
